import { computeAuthRedirectUrl } from "@/components/features/auth/util/computeAuthRedirect";
import { sanitizeFirebaseError } from "@/components/features/auth/util/sanitizeFirebaseError";
import { createUser } from "@/components/features/users/api/users.client";
import { LocationPreferences } from "@/gen/ts/v1/user/user_service_pb";
import { analytics } from "@/lib/analytics/ga4";
import { createLogger } from "@/lib/observability/logger";
import {
  AuthError,
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  User,
} from "firebase/auth";
import { refreshSession } from "./refresh.client";

export type SignUpUserWithPasswordResult =
  | {
      finalRedirectUrl: string;
      userId: string;
    }
  | never;

export async function signUpUserWithPassword({
  email,
  password,
  username,
  locationPreferences,
  redirectUrl,
}: {
  email: string;
  password: string;
  username: string;
  locationPreferences: LocationPreferences;
  redirectUrl?: string;
}): Promise<SignUpUserWithPasswordResult> {
  const logger = createLogger("createUserWithPassword");
  const auth = getAuth();

  let firebaseUser: User | null = null;
  // Step 1: Create Firebase user
  try {
    // Create Firebase user
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (!result.user) {
      throw new Error("User not returned from createUserWithEmailAndPassword");
    }
    firebaseUser = result.user;

    analytics.event({
      action: "signup",
      category: "auth",
      custom_parameters: {
        method: "email_password",
        status: "success",
        username,
      },
    });
  } catch (error) {
    const authError = error as AuthError;
    const sanitizedError = sanitizeFirebaseError(authError);
    const { sanitizedMessage, errorCode } = sanitizedError;

    logger.error(
      `Unable to create user account.\n Code: ${errorCode}`,
      error as Error,
      {
        email,
        errorCode,
      },
    );

    analytics.event({
      action: "signup",
      category: "auth",
      custom_parameters: {
        email,
        errorCode,
      },
    });

    throw new Error(sanitizedMessage);
  }

  // Step 2: Create user profile in database
  try {
    // Create user profile in database
    const createUserResult = await createUser({
      email,
      username,
      locationPreferences,
    });

    if (!createUserResult.userId) {
      throw new Error("User profile not returned from createUser");
    }
  } catch (error) {
    await deleteUser(firebaseUser);

    logger.error("Unable to create user profile in database", error as Error, {
      email,
      username,
      locationPreferences,
    });
    throw new Error(
      "Unable to create user profile. Please try again or contact support.",
    );
  }

  refreshSession();

  // Step 3: Build redirect url
  const finalRedirectUrl = computeAuthRedirectUrl({
    wasCreated: true,
    generatedUsername: null,
    userProfile: null,
    redirectUrl: redirectUrl ?? undefined,
  });

  return {
    finalRedirectUrl,
    userId: firebaseUser.uid,
  };
}
